'use client';

import { useState, useEffect } from 'react';
import { BuildingOfficeIcon, KeyIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import DataTable from '../components/DataTable';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Institutions = () => {
	const [institutions, setInstitutions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState({});
	const { user } = useAuth();

	useEffect(() => {
		fetchInstitutions();
	}, []);

	const fetchInstitutions = async () => {
		try {
			setLoading(true);

			// Try to fetch all institutions (admin view) first
			try {
				const response = await apiService.getAllInstitutions();
				if (response.data.status === 200) {
					// Handle the response structure properly
					const institutionsData =
						response.data.data.institutions ||
						response.data.data ||
						[];
					setInstitutions(institutionsData);
					return;
				}
			} catch (adminError) {
				// If admin call fails, show current institution only
				if (user) {
					setInstitutions([user]);
				}
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to fetch institutions';
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	const handleResetApiKey = async (institutionId) => {
		if (
			!window.confirm(
				'Are you sure you want to reset the API key? This will invalidate the current key.',
			)
		) {
			return;
		}

		setActionLoading((prev) => ({ ...prev, [institutionId]: true }));

		try {
			const response = await apiService.resetApiKey(institutionId);

			if (response.data.status === 200) {
				toast.success('API key reset successfully!');
				fetchInstitutions(); // Refresh the list
			} else {
				throw new Error(
					response.data.message || 'Failed to reset API key',
				);
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to reset API key';
			toast.error(message);
		} finally {
			setActionLoading((prev) => ({ ...prev, [institutionId]: false }));
		}
	};

	const columns = [
		{
			header: 'Institution',
			accessor: 'name',
			render: (institution) => (
				<div className='flex items-center'>
					<div className='flex-shrink-0 h-10 w-10'>
						<div className='h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center'>
							<BuildingOfficeIcon className='h-6 w-6 text-primary-600' />
						</div>
					</div>
					<div className='ml-4'>
						<div className='text-sm font-medium text-gray-900'>
							{institution.name}
						</div>
						<div className='text-sm text-gray-500'>
							{institution.contactEmail}
						</div>
					</div>
				</div>
			),
		},
		{
			header: 'Client ID',
			accessor: 'clientId',
			render: (institution) => (
				<span className='font-mono text-sm bg-gray-100 px-2 py-1 rounded'>
					{institution.clientId}
				</span>
			),
		},
		{
			header: 'Contact Info',
			accessor: 'contact',
			render: (institution) => (
				<div>
					<div className='text-sm text-gray-900'>
						{institution.contactEmail}
					</div>
					{institution.contactPhone && (
						<div className='text-sm text-gray-500'>
							{institution.contactPhone}
						</div>
					)}
				</div>
			),
		},
		{
			header: 'Callback URL',
			accessor: 'callbackUrl',
			render: (institution) => (
				<span
					className='text-sm text-gray-500 truncate max-w-xs'
					title={institution.callbackUrl}>
					{institution.callbackUrl}
				</span>
			),
		},
		{
			header: 'Created',
			accessor: 'createdAt',
			render: (institution) =>
				institution.createdAt
					? format(new Date(institution.createdAt), 'MMM dd, yyyy')
					: 'N/A',
		},
		{
			header: 'Actions',
			accessor: 'actions',
			render: (institution) => (
				<div className='flex items-center space-x-2'>
					<button
						onClick={() => handleResetApiKey(institution.id)}
						disabled={actionLoading[institution.id]}
						className='text-primary-600 hover:text-primary-900 disabled:opacity-50 transition-colors'
						title='Reset API Key'>
						{actionLoading[institution.id] ? (
							<LoadingSpinner size='sm' />
						) : (
							<KeyIcon className='h-4 w-4' />
						)}
					</button>
				</div>
			),
		},
	];

	return (
		<div className='space-y-6'>
			{/* Page description */}
			<div>
				<p className='text-sm text-gray-500'>
					Manage institution accounts and API access
				</p>
			</div>

			{/* Current Institution Card (if viewing own institution) */}
			{institutions.length === 1 && institutions[0].id === user?.id && (
				<div className='card bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200'>
					<div className='flex items-center justify-between'>
						<div className='flex items-center'>
							<div className='flex-shrink-0'>
								<div className='w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center'>
									<BuildingOfficeIcon className='h-8 w-8 text-white' />
								</div>
							</div>
							<div className='ml-4'>
								<h3 className='text-lg font-medium text-gray-900'>
									Your Institution
								</h3>
								<p className='text-sm text-gray-600'>
									{user.name}
								</p>
							</div>
						</div>
						<div className='text-right'>
							<p className='text-sm text-gray-500'>Client ID</p>
							<p className='font-mono text-sm bg-white px-3 py-1 rounded border'>
								{user.clientId}
							</p>
						</div>
					</div>
				</div>
			)}

			{/* Stats cards (for admin view) */}
			{institutions.length > 1 && (
				<div className='grid grid-cols-1 gap-5 sm:grid-cols-3'>
					<div className='card'>
						<div className='flex items-center'>
							<div className='flex-shrink-0'>
								<BuildingOfficeIcon className='h-8 w-8 text-primary-600' />
							</div>
							<div className='ml-5 w-0 flex-1'>
								<dl>
									<dt className='text-sm font-medium text-gray-500 truncate'>
										Total Institutions
									</dt>
									<dd className='text-lg font-medium text-gray-900'>
										{institutions.length}
									</dd>
								</dl>
							</div>
						</div>
					</div>

					<div className='card'>
						<div className='flex items-center'>
							<div className='flex-shrink-0'>
								<div className='w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center'>
									<span className='text-green-600 font-semibold text-sm'>
										✓
									</span>
								</div>
							</div>
							<div className='ml-5 w-0 flex-1'>
								<dl>
									<dt className='text-sm font-medium text-gray-500 truncate'>
										Active Today
									</dt>
									<dd className='text-lg font-medium text-gray-900'>
										{institutions.length}{' '}
										{/* Placeholder - would need actual activity data */}
									</dd>
								</dl>
							</div>
						</div>
					</div>

					<div className='card'>
						<div className='flex items-center'>
							<div className='flex-shrink-0'>
								<KeyIcon className='h-8 w-8 text-yellow-600' />
							</div>
							<div className='ml-5 w-0 flex-1'>
								<dl>
									<dt className='text-sm font-medium text-gray-500 truncate'>
										API Keys Issued
									</dt>
									<dd className='text-lg font-medium text-gray-900'>
										{institutions.length}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Institutions table */}
			<DataTable
				columns={columns}
				data={institutions}
				loading={loading}
				emptyMessage='No institutions found'
			/>

			{/* API Key Information */}
			<div className='card bg-yellow-50 border-yellow-200'>
				<div className='flex'>
					<div className='flex-shrink-0'>
						<KeyIcon className='h-5 w-5 text-yellow-400' />
					</div>
					<div className='ml-3'>
						<h3 className='text-sm font-medium text-yellow-800'>
							API Key Security
						</h3>
						<div className='mt-2 text-sm text-yellow-700'>
							<ul className='list-disc pl-5 space-y-1'>
								<li>
									Keep your API keys secure and never share
									them publicly
								</li>
								<li>
									Reset your API key immediately if you
									suspect it has been compromised
								</li>
								<li>
									Use HTTPS for all API requests to ensure
									secure communication
								</li>
								<li>
									Monitor your API usage regularly for any
									suspicious activity
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Institutions;
